import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const CreateBranchForm = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    branch_name: "",
    description: "",
    customer_id: "",
  });
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (!tenant_id) return;

    // Load customers
    secureAxios
      .get(`/api/customers?tenant_id=${tenant_id}`)
      .then((res) => {
        setCustomers(res.data);
      })
      .catch((err) => {
        console.error("Error loading customers:", err);
        setMessage("Failed to load customers");
      });
  }, [tenant_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      if (!formData.branch_name.trim()) {
        setMessage("Branch/Office name is required");
        setLoading(false);
        return;
      }

      const payload = {
        branch_name: formData.branch_name.trim(),
        description: formData.description.trim(),
        customer_id: formData.customer_id || null,
        tenant_id,
        created_by: user?.user_id,
      };

      await secureAxios.post("/api/branches", payload);

      setMessage("Branch/Office created successfully!");

      // Reset form
      setFormData({
        branch_name: "",
        description: "",
        customer_id: "",
      });

      if (onCreated) {
        setTimeout(() => onCreated(), 1000);
      }
    } catch (err) {
      console.error("Error creating branch:", err);
      const backendMessage =
        err?.response?.data?.error || "Failed to create branch";
      setMessage(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {message && (
        <div
          className={`alert ${
            message.includes("successfully")
              ? "alert-success"
              : "alert-danger"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-12 col-md-6">
          <label className="form-label">
            Branch/Office Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="branch_name"
            value={formData.branch_name}
            onChange={handleChange}
            placeholder="e.g., Downtown Office, North Region"
            required
            disabled={loading}
            maxLength="100"
          />
          <div className="form-text">
            This name will appear in the "Bill To" section on invoices
          </div>
        </div>

        <div className="col-12 col-md-6">
          <label className="form-label">Customer (Optional)</label>
          <select
            className="form-select"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">All Customers (Global)</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="form-text">
            Leave blank to make this branch available for all customers
          </div>
        </div>

        <div className="col-12">
          <label className="form-label">Description (Optional)</label>
          <textarea
            className="form-control"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of this branch or office location"
            rows="2"
            disabled={loading}
            maxLength="255"
          />
        </div>

        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary w-100 w-md-auto"
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-2"></i>
                Create Branch/Office
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateBranchForm;